import {Form, Modal, Row, Col, Select, Table, Tag} from "antd";
import {useFormik} from "formik";
import React, {useState} from "react";
import {useTranslation} from "react-i18next";
import * as yup from "yup";
import * as Sentry from "@sentry/react";
import {useFetch} from "../../../../../../../hooks/useFetch";
import {useStore} from "../../../../../../../hooks/useStore";
import {compareString, getLocaleLang} from "../../../../../../../utils/helpers";
import {PermissionPolicyList} from "../../../permissionPolicyList/PermissionPolicyList";

const AllowedPermissionPoliciesAssignModal = ({
    userId,
    allowedPermissionPolicies = [],
    visibility,
    onSuccess,
    onCancel
}) => {
    const {t, i18n} = useTranslation(["userAuthorization"]);
    const stores = useStore();
    const [confirmLoading, setConfirmLoading] = useState();

    const [permissionPoliciesLoading, permissionPolicies] = useFetch({
        defaultDataValue: [],
        fetchFn: stores.authorizationStore.listPermissionPolicies.bind(null, {userId}),
        dependencies: [visibility],
        initialCall: true,
        condition: visibility
    });

    const formik = useFormik({
        initialValues: {userId: userId, permissionPolicyKeys: []},
        validationSchema: yup.object().shape({
            permissionPolicyKeys: yup.array().required(t("validation.required", {ns: "common"}))
        }),
        enableReinitialize: true,
        onSubmit: async values => {
            setConfirmLoading(true);
            try {
                const assignPermissionPolicesResult = await stores.userStore.assignAllowedPermissionPolicies(
                    {
                        userId,
                        permissionPolicyKeys: values.permissionPolicyKeys
                    }
                );
                if (!assignPermissionPolicesResult.success) return;
                reset();
                onSuccess();
            } catch (error) {
                Sentry.captureException(error);
                onCancel();
            } finally {
                setConfirmLoading(false);
            }
        }
    });

    const {values, errors, touched, handleSubmit, setFieldValue} = formik;

    const reset = () => {
        setFieldValue("permissionPolicyKeys", []);
    };

    return (
        <Modal
            title={t("modals.allowedPermissionPoliciesAssignModal.assignPermissionPolicies", {
                ns: "userAuthorization"
            })}
            visible={visibility}
            onCancel={() => {
                reset();
                onCancel();
            }}
            width={1000}
            onOk={handleSubmit}
            confirmLoading={confirmLoading}
            okText={t("modal.sendButton", {ns: "common"})}
            cancelText={t("modal.cancelButton2", {ns: "common"})}
        >
            <Form labelCol={{span: 24}} wrapperCol={{span: 24}}>
                <Row>
                    <Col span={24}>
                        <Form.Item
                            label={t("common.permissionPolicies", {ns: "userAuthorization"})}
                            validateStatus={
                                errors.permissionPolicyKeys && touched.permissionPolicyKeys
                                    ? "error"
                                    : "success"
                            }
                            help={
                                errors.permissionPolicyKeys && touched.permissionPolicyKeys
                                    ? errors.permissionPolicyKeys
                                    : null
                            }
                        >
                            <Select
                                showSearch
                                placeholder={t("common.selectPermissionPolicy", {
                                    ns: "userAuthorization"
                                })}
                                optionFilterProp="children"
                                optionLabelProp="label"
                                value={values.permissionPolicyKeys}
                                onChange={value => setFieldValue("permissionPolicyKeys", value)}
                                filterOption={(input, option) =>
                                    compareString(option?.content, input)
                                }
                                mode="multiple"
                                loading={permissionPoliciesLoading}
                            >
                                {permissionPolicies &&
                                    permissionPolicies?.rows?.map(permissionPolicy => {
                                        return (
                                            <Select.Option
                                                key={permissionPolicy.key}
                                                label={permissionPolicy.name[getLocaleLang(i18n)]}
                                                value={permissionPolicy.key}
                                                disabled={allowedPermissionPolicies
                                                    .map(aPP => aPP.key)
                                                    .includes(permissionPolicy.key)}
                                                content={` ${permissionPolicy.key} ${
                                                    permissionPolicy.name[getLocaleLang(i18n)]
                                                } ${
                                                    permissionPolicy.description[
                                                        getLocaleLang(i18n)
                                                    ]
                                                } ${permissionPolicy.tags?.join(" ")}}`}
                                            >
                                                <Row>
                                                    {permissionPolicy.name[getLocaleLang(i18n)]}{" "}
                                                    <Col
                                                        span={24}
                                                        style={{
                                                            opacity: 0.8,
                                                            fontSize: "12px",
                                                            whiteSpace: "normal"
                                                        }}
                                                    >
                                                        {
                                                            permissionPolicy.description[
                                                                getLocaleLang(i18n)
                                                            ]
                                                        }
                                                    </Col>
                                                </Row>
                                            </Select.Option>
                                        );
                                    })}
                            </Select>
                        </Form.Item>
                    </Col>
                    {values.permissionPolicyKeys && values.permissionPolicyKeys?.length > 0 && (
                        <Col span={24}>
                            <h4>
                                {t("common.selectedPermissionPolicies", {
                                    ns: "userAuthorization"
                                })}
                            </h4>
                            <PermissionPolicyList
                                permissionPolicies={
                                    permissionPolicies?.rows?.filter(permissionPolicy =>
                                        values.permissionPolicyKeys.includes(permissionPolicy.key)
                                    ) || []
                                }
                            />
                        </Col>
                    )}
                </Row>
            </Form>
        </Modal>
    );
};

export default AllowedPermissionPoliciesAssignModal;
